import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { MessageSquare, Phone, PhoneOff, PhoneIncoming, Wallet, Landmark, Settings, Send, Loader2, ChevronLeft, Mic, MicOff, UserRound, Wifi, BatteryFull, CheckCheck, Users, Camera, TrendingUp, TrendingDown, Heart, AtSign, Siren, ImagePlus, X, ShoppingBag, Leaf, Ghost, Sprout, Mail, Store, CarFront, CloudSun, Radio, NotebookPen, Package, CarTaxiFront, Building2, Compass, HeartPulse, ShieldCheck as ShieldLock } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PreviewRibbon, DEMO } from "@/components/cef/cefShared";
import { activePack } from "@/components/cef/packs";
import { usePhoneAudio } from "@/components/cef/phoneAudio";
import SpecStoreApp from "@/components/cef/phoneStore";
import GreenPagesApp from "@/components/cef/phoneLeaf";
import GhostwireApp from "@/components/cef/phoneWire";
import MailApp from "@/components/cef/phoneMail";
import PawnApp from "@/components/cef/phonePawn";
import DmvApp from "@/components/cef/phoneDmv";
import CitizenSheet from "@/components/cef/phoneCitizen";
import PhoneBoot from "@/components/cef/phoneBoot";
import LockScreen, { PinPad, PatternPad, ThumbScan } from "@/components/cef/phoneLock";
import NotificationShade from "@/components/cef/phoneShade";
import SkylineApp from "@/components/cef/phoneSkyline";
import RadioApp from "@/components/cef/phoneRadio";
import LiftApp from "@/components/cef/phoneLift";
import CivicApp from "@/components/cef/phoneCivic";
import AtlasApp from "@/components/cef/phoneAtlas";
import PulseApp from "@/components/cef/phonePulse";
import MediCardApp from "@/components/cef/phoneMedical";
import GrowTrackApp from "@/components/cef/phoneGrowTrack";
import GhostPakApp from "@/components/cef/phoneGhostPak";
import NotesApp from "@/components/cef/phoneNotes";
import PhoneHome from "@/components/cef/phoneHome";
import MessagesApp from "@/components/cef/phoneMessages";
import { CallsApp, IncomingCall } from "@/components/cef/phoneCalls";
import { useSpamPhone } from "@/components/cef/phoneSpam";
import { PHONE_THEMES, THEME_FILTERS } from "@/components/cef/phoneThemes";
import { useI18n } from "@/i18n";

/* In-game PHONE OS (CEF) — /cef/phone/:serverId?token=<player-session>.
   Apps: Messages (DMs + group chats + photo snaps), Calls (VoIP citizen-to-citizen
   + 911 AI dispatcher), Buzzer (social feed), Market (goods & stocks), Bank,
   City Hall (vote + mayor powers), Settings (wallpaper/accent/ringtone/notifications).
   No token (browser preview) → fully clickable demo. */

const WALLPAPERS = {
  midnight: { label: "Midnight", css: "linear-gradient(160deg,#0b1220,#05070d 60%,#0a0f1c)" },
  sunset: { label: "Sunset Blvd", css: "linear-gradient(160deg,#3b1d4e,#8a2f3f 55%,#e07b39)" },
  neon: { label: "Neon Grid", css: "linear-gradient(160deg,#031b1f,#0a3b46 50%,#0e6653)" },
  matrix: { label: "Matrix", css: "linear-gradient(160deg,#02110a,#032c1a 60%,#02110a)" },
  crimson: { label: "Crimson", css: "linear-gradient(160deg,#1c0508,#4a0d18 60%,#7a1220)" },
  ocean: { label: "Ocean", css: "linear-gradient(160deg,#041626,#0a3550 55%,#0f5e6b)" },
  aurora: { label: "Aurora", css: "linear-gradient(160deg,#120a2e,#233b7a 55%,#2e8b8b)" },
  mono: { label: "Mono", css: "linear-gradient(160deg,#0a0a0a,#1a1a1a 60%,#050505)" },
};
const ACCENTS = ["#2E6BFF", "#ff8a2e", "#22c55e", "#e8b93e", "#ef4444", "#a78bfa"];
const RINGTONES = [["pulse", "Pulse"], ["synth", "Synthwave"], ["classic", "Classic Bell"]];
const wpStyle = (wp) => WALLPAPERS[wp] ? { background: WALLPAPERS[wp].css } : { background: `url(${wp}) center/cover` };
const iso = (minAgo) => new Date(Date.now() - minAgo * 60000).toISOString();

const DEMO_HOME = {
  me: { playercode: 37049, name: "Demo Citizen" },
  profile: { wallpaper: "midnight", accent: "#2E6BFF", notifications: true, ringtone: "pulse", theme: "stock", security: null, setup_done: true },
  unread: 2,
  mail_unread: 2,
  convos: [
    { peer_pc: 22110, peer_name: "Ada King", last: "meet at city hall in 10? polls close soon", at: iso(6), unread: 2 },
    { peer_pc: 88014, peer_name: "Mia Torres", last: "night market permit got approved!!", at: iso(52), unread: 0 },
    { peer_pc: 22111, peer_name: "Ray Dalton", last: "your cab is outside", at: iso(190), unread: 0 },
  ],
  npc_contacts: [{ ref: "CTZ-DEMO01", name: "Emeka Abernathy", occupation: "teacher", suburb: "North Market", shared_at: iso(80) }],
  npc_convos: [{ ref: "CTZ-DEMO01", name: "Emeka Abernathy", occupation: "teacher", last: "Saved. It's Emeka — text me when you're around.", at: iso(22), unread: 1, kind: "problem", checkin: true }],
  groups: [{ id: "g1", name: "Downtown Crew", members: [37049, 22110, 22111], member_names: {}, last: "ada: convoy at 9?", last_at: iso(12) }],
  incoming: null, call: null,
};
const DEMO_CONTACTS = [
  { playercode: 22110, name: "Ada King" }, { playercode: 22111, name: "Ray Dalton" },
  { playercode: 88014, name: "Mia Torres" }, { playercode: 11204, name: "R. O'Connor" },
];
const DEMO_THREAD = [
  { id: "m1", from_pc: 22110, body: "yo, you voting tonight?", at: iso(40) },
  { id: "m2", from_pc: 37049, body: "yeah, still deciding tbh", at: iso(38) },
  { id: "m3", from_pc: 22110, body: "meet at city hall in 10? polls close soon", at: iso(6) },
];
const DEMO_BLEETS = [
  { id: "b1", author_pc: 22110, author_name: "Ada King", handle: "ada_king", body: "Polls are OPEN. This city deserves a funded EMS and a budget you can actually read. #NCElection", likes: 41, liked: false, at: iso(35), image_url: "" },
  { id: "b2", author_pc: 88014, author_name: "Mia Torres", handle: "mia_torres", body: "night market permit APPROVED. see you all friday, bring cash and good vibes", likes: 23, liked: true, at: iso(90), image_url: "" },
  { id: "b3", author_pc: 22111, author_name: "Ray Dalton", handle: "ray_dalton", body: "gas prices up again?? gold down?? this market makes no sense, anyway fares are fair at Downtown Cab", likes: 8, liked: false, at: iso(150), image_url: "" },
];
const DEMO_MARKET = {
  goods: [
    { key: "gold", name: "Gold", price: 1241.5, change: 2.31, history: [1200, 1210, 1202, 1215, 1228, 1219, 1233, 1241.5], low: 800, high: 2200 },
    { key: "fuel", name: "Fuel", price: 86.1, change: -1.84, history: [90, 88, 89, 87, 88, 86.1], low: 40, high: 180 },
    { key: "medicine", name: "Medicine", price: 271.2, change: 0.74, history: [268, 269, 270, 269, 271.2], low: 140, high: 540 },
    { key: "electronics", name: "Electronics", price: 318, change: 3.02, history: [300, 305, 310, 309, 314, 318], low: 180, high: 620 },
  ],
  stocks: [
    { key: "LNX", name: "Lynx Motors", price: 214.8, change: 1.12, history: [208, 210, 211, 213, 214.8], low: 90, high: 520 },
    { key: "BUZZ", name: "Buzzer Inc", price: 99.4, change: 4.86, history: [92, 95, 94, 97, 99.4], low: 30, high: 300 },
    { key: "MZB", name: "Maze Bank", price: 176.2, change: -0.92, history: [180, 179, 178, 177, 176.2], low: 80, high: 420 },
  ],
  tax_sales: 10,
};

function StatusBar({ accent, onShade }) {
  const [now, setNow] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 15000); return () => clearInterval(t); }, []);
  return (
    <button data-testid="phone-statusbar" onClick={onShade} className="w-full flex items-center justify-between px-6 pt-3 pb-1 font-mono text-[10px] text-white/85 cursor-pointer" title="Pull down for notifications">
      <span data-testid="phone-clock">{now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
      <span className="w-24 h-5 bg-black rounded-b-2xl -mt-3 border-b border-x border-white/10" />
      <span className="flex items-center gap-1.5"><Wifi className="w-3 h-3" /><BatteryFull className="w-3.5 h-3.5" style={{ color: accent }} /></span>
    </button>
  );
}

export default function PhoneCefPage() {
  const { serverId } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const demo = !token;
  const sfx = usePhoneAudio();
  const { t } = useI18n();

  const [home, setHome] = useState(null);
  const [app, setApp] = useState(null);
  const [sos, setSos] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [now, setNow] = useState(new Date());
  const [dealState, setDealState] = useState(null); // GreenPages/Ghostwire installs + Dealer 101
  const [wireThread, setWireThread] = useState(null); // cross-app jump: GreenPages profile → Ghostwire thread
  const [citizenSheet, setCitizenSheet] = useState(null); // {pc, name} — Buzzer author → citizen file
  const [booted, setBooted] = useState(false);
  const [locked, setLocked] = useState(false);
  const [shade, setShade] = useState(false);
  const [spam, setSpam] = useState([]); // ambient scam/wrong-number/missed-call entries in the shade
  const [banner, setBanner] = useState(null); // transient top banner for incoming spam
  const [scamCall, setScamCall] = useState(null);
  const prevUnread = useRef(0);

  const dealApps = dealState?.apps || {};

  // CEF overlay: the 3D world shows around the handset — body/html must stay transparent
  useEffect(() => {
    document.documentElement.style.background = "transparent";
    document.body.style.background = "transparent";
    return () => { document.documentElement.style.background = ""; document.body.style.background = ""; };
  }, []);

  const loadDealer = useCallback(() => {
    if (demo) {
      setDealState((s) => s || { apps: { greenpages: false, ghostwire: false }, first_weed: true,
        tutorial: { install_greenpages: false, install_ghostwire: false, open_shop: false, first_listing: false, first_sale: false },
        wire_unread: 1, orders_open: 0, shop_orders: 0 });
      return;
    }
    api.get(`/nc/servers/${serverId}/leaf/state`, { params: { token } })
      .then((r) => setDealState(r.data)).catch(() => {});
  }, [demo, serverId, token]);

  const profile = home?.profile || DEMO_HOME.profile;
  const accent = profile.accent || "#2E6BFF";
  const me = home?.me || DEMO_HOME.me;
  // CivicOS themes — owned via SpecStore (stock + high-contrast free for everyone)
  const ownedThemes = demo ? Object.keys(PHONE_THEMES)
    : ["stock", "contrast", ...(dealApps.theme_stealth ? ["stealth"] : []), ...(dealApps.theme_light ? ["light"] : [])];
  const themeFilter = THEME_FILTERS[profile.theme || "stock"] || "";

  /* optimistic profile apply — fixes "settings don't change the background": the
     moment you pick a wallpaper/accent the handset re-skins, then it persists. */
  const applyProfile = useCallback((patch) => {
    setHome((h) => (h ? { ...h, profile: { ...h.profile, ...patch } } : h));
  }, []);

  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);

  // audio unlock on first touch + boot chime
  useEffect(() => {
    const unlock = () => sfx.ensure();
    window.addEventListener("pointerdown", unlock, { once: true });
    window.addEventListener("keydown", unlock, { once: true });
    sfx.bootChime();
    return () => { window.removeEventListener("pointerdown", unlock); window.removeEventListener("keydown", unlock); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadHome = useCallback(() => {
    if (demo) { setHome((h) => h || DEMO_HOME); setContacts(DEMO_CONTACTS); return; }
    api.get(`/nc/servers/${serverId}/phone/home`, { params: { token } })
      .then((r) => { setHome((h) => ({ ...r.data, profile: { ...r.data.profile, ...(h?.profileLocal || {}) } })); })
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
    api.get(`/nc/servers/${serverId}/phone/contacts`, { params: { token } }).then((r) => setContacts(r.data.contacts)).catch(() => {});
  }, [demo, serverId, token]);

  useEffect(() => { loadDealer(); }, [loadDealer, app === null]); // refresh installs/badges when returning home

  useEffect(() => {
    loadHome();
    const t = setInterval(loadHome, 4000);
    return () => clearInterval(t);
  }, [loadHome]);

  // ding when unread goes up
  useEffect(() => {
    if (home && home.unread > prevUnread.current) sfx.receiveDing();
    prevUnread.current = home?.unread || 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [home?.unread]);

  // incoming-call ringtone (profile-selected)
  const incoming = home?.incoming;
  useEffect(() => {
    if (!incoming || !profile.notifications) { sfx.stopRing(); return; }
    sfx.startRing(profile.ringtone || "pulse");
    return () => sfx.stopRing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incoming?.id, profile.notifications, profile.ringtone]);

  const openApp = useCallback((id) => { sfx.appOpen(); setApp(id); }, [sfx]);
  const goHome = () => { sfx.back(); setApp(null); };

  const saveProfile = async (patch) => {
    applyProfile(patch); // instant — the phone re-skins immediately
    if (demo) { toast.success("Preview: phone personalized (preview doesn't persist — in-game it saves to your citizen)"); return true; }
    const next = { wallpaper: profile.wallpaper, accent: profile.accent, notifications: profile.notifications, ringtone: profile.ringtone || "pulse", theme: profile.theme || "stock", security: profile.security ?? null, ...patch };
    try {
      await api.put(`/nc/servers/${serverId}/phone/settings`, next, { params: { token } });
      loadHome();
      return true;
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); loadHome(); return false; }

  };

  const homeApps = useMemo(() => {
    const item = (id, label, Icon, color, badge = 0, onOpen = null) => ({ id, label, Icon, color, badge, onOpen: onOpen || (() => openApp(id)) });
    const list = [
      item("messages", t("ph.messages"), MessageSquare, accent, home?.unread || 0),
      item("calls", t("ph.calls"), Phone, "#22c55e"),
      item("buzzer", "Buzzer", AtSign, "#38bdf8"),
      item("market", t("ph.market"), TrendingUp, "#f59e0b"),
      item("bank", t("ph.bank"), Wallet, accent),
      item("cityhall", t("ph.cityhall"), Landmark, "#e8b93e"),
      item("sos", "911", Siren, "#ef4444", 0, () => { sfx.appOpen(); setSos(true); }),
      item("settings", t("ph.settings"), Settings, "#a1a1aa"),
      item("store", "SpecStore", ShoppingBag, "#f472b6"),
      item("mail", "Mail", Mail, "#e8b93e", demo ? DEMO_HOME.mail_unread : (home?.mail_unread || 0)),
      item("notes", "Notes", NotebookPen, "#fbbf24"),
      item("civic", "Services", Building2, "#94a3b8"),
      item("dmv", "DMV", CarFront, "#60a5fa"),
      item("atlas", "Atlas", Compass, "#38bdf8"),
      item("skyline", "Skyline", CloudSun, "#7dd3fc"),
      item("radio", "Boombox", Radio, "#fb7185"),
      item("pawn", "Pawn", Store, "#f59e0b"),
      item("lift", "Lift", CarTaxiFront, "#facc15"),
      item("pulse", "Pulse", Heart, "#ec4899"),
      item("medicard", "MediCard", HeartPulse, "#ef4444"),
      item("ghostpak", "Ghost-Pak", Package, "#f97316"),
    ];
    if (dealApps.greenpages) list.push(item("greenpages", "GreenPages", Leaf, "#4ade80", dealState?.orders_open || 0));
    if (dealApps.ghostwire) list.push(item("ghostwire", "Ghostwire", Ghost, "#38bdf8", dealState?.wire_unread || 0));
    if (dealApps.growtrack) list.push(item("growtrack", "GrowTrack", Sprout, "#a3e635"));
    return list;
  }, [accent, dealApps.greenpages, dealApps.ghostwire, dealApps.growtrack, dealState?.orders_open, dealState?.wire_unread, demo, home?.mail_unread, home?.unread, openApp, sfx, t]);

  const dockApps = [
    { id: "messages", Icon: MessageSquare }, { id: "calls", Icon: Phone },
    { id: "buzzer", Icon: AtSign }, { id: "settings", Icon: Settings },
  ];

  const markInstalled = useCallback((id) => {
    if (demo) {
      setDealState((s) => ({ ...(s || {}), apps: { ...(s?.apps || {}), [id]: true } }));
      return;
    }
    loadDealer();
  }, [demo, loadDealer]);

  const saveHomeLayout = useCallback(async (pages, folders = {}) => {
    applyProfile({ home_layout: pages, home_folders: folders });
    if (demo) return true;
    try {
      await api.put(`/nc/servers/${serverId}/phone/home-layout`, { pages, folders }, { params: { token } });
      return true;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      loadHome();
      return false;
    }
  }, [applyProfile, demo, loadHome, serverId, token]);

  // ambient phone noise: scam calls, spam texts, wrong numbers — rare, clearly dodgy
  const pushSpam = (item) => {
    sfx.dispatchChirp();
    setSpam((s) => [item, ...s].slice(0, 8));
    setBanner(item);
    setTimeout(() => setBanner((b) => (b && b.id === item.id ? null : b)), 4500);
  };
  useSpamPhone({
    enabled: booted && !locked && profile.notifications,
    callBusy: !!(incoming || sos || scamCall),
    onText: pushSpam,
    onCall: (call) => { sfx.startRing(profile.ringtone); setScamCall(call); },
  });

  /* scam gamble — answering starts a hidden server-side timer. Hang up inside the
     safe window and nothing happens; stay on the line and the "caller" drains
     your bank (FRAUD DEBIT). The server measures elapsed time, never the client. */
  const [activeScam, setActiveScam] = useState(null); // {id, line, at}
  const resolveRef = useRef(null);

  const resolveScam = async (s) => {
    if (!s) return;
    setActiveScam(null);
    const elapsed = (Date.now() - s.at) / 1000;
    if (demo) {
      if (elapsed > 8) {
        const amt = 40 + Math.floor(Math.random() * 211);
        sfx.error();
        pushSpam({ id: `fd-${s.id}`, kind: "scam", body: `FRAUD DEBIT — $${amt} · 'Bornwood Revenue Service' — preview only, your demo bank is safe.` });
      } else toast.success("Hung up in time — they got nothing (preview)");
      return;
    }
    try {
      const r = await api.post(`/nc/servers/${serverId}/phone/scam/hangup`, { id: s.id }, { params: { token } });
      if (r.data.hit > 0) {
        sfx.error();
        toast.error(`FRAUD DEBIT — $${r.data.hit} · they spoofed your banking app`);
        pushSpam({ id: `fd-${s.id}`, kind: "scam", body: `FRAUD DEBIT — $${r.data.hit} · 'Bornwood Revenue Service' — Bornwood Mutual flagged this as fraud.` });
        loadHome();
      } else toast.success(r.data.message);
    } catch { /* session gone server-side — nothing to resolve */ }
  };
  resolveRef.current = resolveScam;

  const answerScam = async () => {
    sfx.stopRing(); sfx.keyTick();
    const call = scamCall; setScamCall(null);
    if (!call) return;
    let id = `demo-${call.id}`;
    if (!demo) {
      try {
        const r = await api.post(`/nc/servers/${serverId}/phone/scam/answer`, {}, { params: { token } });
        id = r.data.id;
      } catch { return; }
    }
    setActiveScam({ id, line: call.line, at: Date.now() });
  };

  // they hang up on you after 20s — resolving whatever damage the timer says
  useEffect(() => {
    if (!activeScam) return undefined;
    const t = setTimeout(() => resolveRef.current?.(activeScam), 20000);
    return () => clearTimeout(t);
  }, [activeScam]);

  const bootDone = () => {
    setBooted(true);
    if (profile.security?.method) setLocked(true);
  };

  return (
    <div data-testid="phone-cef" className="min-h-screen bg-black flex items-center justify-center p-4 relative overflow-hidden">
      <style>{`@keyframes phShadeDown{from{transform:translateY(-104%)}to{transform:translateY(0)}}@keyframes phShake{0%,100%{transform:translateX(0)}20%{transform:translateX(-9px)}40%{transform:translateX(9px)}60%{transform:translateX(-6px)}80%{transform:translateX(6px)}}@keyframes phAppIn{from{opacity:0;transform:scale(0.93) translateY(10px)}to{opacity:1;transform:scale(1) translateY(0)}}@keyframes phBannerIn{from{opacity:0;transform:translateY(-16px) scale(0.97)}to{opacity:1;transform:translateY(0) scale(1)}}`}</style>
      {demo && <PreviewRibbon what="phone os" />}
      <div className="relative w-[392px] h-[800px] max-h-[94vh] rounded-[3rem] border-[6px] border-zinc-800 bg-black shadow-[0_40px_120px_rgba(0,0,0,0.9)] overflow-hidden flex flex-col transition-all duration-500" style={{ ...wpStyle(profile.wallpaper), filter: [activePack("phone_theme")?.pack?.phone?.filter, themeFilter].filter(Boolean).join(" ") || undefined }}>
        <div className="absolute inset-0 bg-black/25 pointer-events-none" />
        <StatusBar accent={accent} onShade={() => { if (booted && !locked) { sfx.keyTick(); setShade((s) => !s); } }} />

        <div className="relative flex-1 min-h-0 flex flex-col px-4 pb-2">
          {!home && <div className="flex-1 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin" style={{ color: accent }} /></div>}

          {home && !demo && !profile.setup_done && (
            <SetupWizard accent={accent} sfx={sfx} onDone={async (s) => {
              const ok = await saveProfile({ ...s, setup_done: true });
              if (ok) { sfx.success(); toast.success("Phone activated — welcome to your city"); }
            }} />
          )}

          {home && (demo || profile.setup_done) && !app && !sos && (
            <PhoneHome apps={homeApps} dock={dockApps} profile={profile} me={me} now={now}
                       accent={accent} onSaveLayout={saveHomeLayout} />
          )}

          {home && app && (
            <div className="relative flex-1 min-h-0 flex flex-col rounded-[2rem] bg-black/60 backdrop-blur-xl border border-white/10 overflow-hidden">
              <div className="flex items-center gap-2 px-4 py-3 border-b border-white/10">
                <button data-testid="phone-back-home" onClick={goHome} className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.2em] text-white/60 hover:text-white transition-colors"><ChevronLeft className="w-4 h-4" /> {t("ph.home")}</button>
                <p className="mx-auto font-mono text-[10px] uppercase tracking-[0.3em] text-white/80">{{ store: "SpecStore", greenpages: "GreenPages", ghostwire: "Ghostwire", mail: "City Mail", pawn: "Goldman & Sons", dmv: "Bornwood DMV", skyline: "Skyline", radio: "Boombox", notes: "Notes", growtrack: "GrowTrack", ghostpak: "Ghost-Pak", lift: "Lift", civic: "City Services", atlas: "Atlas", pulse: "Pulse", medicard: "MediCard" }[app] || (app === "cityhall" ? t("ph.cityhall") : t(`ph.${app}`))}</p>
                <span className="w-10" />
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto" style={{ scrollbarWidth: "thin" }} key={app}>
                <div style={{ animation: "phAppIn 0.28s cubic-bezier(0.2, 0.9, 0.3, 1) both" }}>
                {app === "messages" && <MessagesApp demo={demo} serverId={serverId} token={token} home={home} contacts={contacts} me={me} accent={accent} sfx={sfx} onChanged={loadHome} />}
                {app === "calls" && <CallsApp demo={demo} serverId={serverId} token={token} home={home} contacts={contacts} accent={accent} sfx={sfx} onChanged={loadHome} onSos={() => setSos(true)} />}
                {app === "buzzer" && <BuzzerApp demo={demo} serverId={serverId} token={token} me={me} accent={accent} sfx={sfx} onProfile={setCitizenSheet} />}
                {app === "market" && <MarketApp demo={demo} serverId={serverId} token={token} accent={accent} />}
                {app === "bank" && <BankApp demo={demo} serverId={serverId} token={token} accent={accent} />}
                {app === "cityhall" && <CityHallApp demo={demo} serverId={serverId} token={token} sfx={sfx} />}
                {app === "settings" && <SettingsApp demo={demo} profile={profile} accent={accent} sfx={sfx} ownedThemes={ownedThemes} onSave={saveProfile} />}
                {app === "store" && <SpecStoreApp demo={demo} serverId={serverId} token={token} accent={accent} installed={Object.keys(dealApps).filter((k) => dealApps[k])} onInstalled={markInstalled} onOpen={(id) => id.startsWith("theme_") ? openApp("settings") : openApp(id)} />}
                {app === "greenpages" && <GreenPagesApp demo={demo} serverId={serverId} token={token} accent={accent} sfx={sfx}
                  onMessageVendor={(npcId) => { setWireThread(npcId); openApp("ghostwire"); }} />}
                {app === "ghostwire" && <GhostwireApp demo={demo} serverId={serverId} token={token} accent={accent} sfx={sfx} initialThread={wireThread} />}
                {app === "mail" && <MailApp demo={demo} serverId={serverId} token={token} accent={accent} sfx={sfx} />}
                {app === "pawn" && <PawnApp demo={demo} serverId={serverId} token={token} accent={accent} sfx={sfx} />}
                {app === "dmv" && <DmvApp demo={demo} serverId={serverId} token={token} accent={accent} sfx={sfx} />}
                {app === "skyline" && <SkylineApp demo={demo} serverId={serverId} accent={accent} />}
                {app === "radio" && <RadioApp accent={accent} sfx={sfx} />}
                {app === "growtrack" && <GrowTrackApp demo={demo} serverId={serverId} token={token} accent={accent} />}
                {app === "ghostpak" && <GhostPakApp demo={demo} serverId={serverId} token={token} accent={accent} />}
                {app === "notes" && <NotesApp serverId={serverId} pc={me.playercode} accent={accent} sfx={sfx} />}
                {app === "lift" && <LiftApp demo={demo} serverId={serverId} token={token} accent={accent} sfx={sfx} />}
                {app === "civic" && <CivicApp demo={demo} serverId={serverId} token={token} accent={accent} sfx={sfx} />}
                {app === "atlas" && <AtlasApp demo={demo} serverId={serverId} token={token} accent={accent} sfx={sfx} />}
                {app === "pulse" && <PulseApp demo={demo} serverId={serverId} token={token} accent={accent} sfx={sfx} />}
                {app === "medicard" && <MediCardApp demo={demo} serverId={serverId} token={token} accent={accent} sfx={sfx} />}
                </div>
              </div>
              {citizenSheet && <CitizenSheet demo={demo} serverId={serverId} token={token} pc={citizenSheet.pc} name={citizenSheet.name} mePc={me.playercode} sfx={sfx} onClose={() => setCitizenSheet(null)} />}
            </div>
          )}

          {sos && <SosCall demo={demo} serverId={serverId} token={token} me={me} sfx={sfx} onClose={() => { setSos(false); }} />}
        </div>

        <button data-testid="phone-home-bar" onClick={() => { setSos(false); goHome(); }} className="relative mx-auto mb-2 mt-1 w-32 h-1.5 rounded-full bg-white/40 hover:bg-white/70 transition-colors" />

        {incoming && <IncomingCall call={incoming} accent={accent} demo={demo} serverId={serverId} token={token} onChanged={loadHome} />}

        {/* boot splash → lock screen → home */}
        {!booted && <PhoneBoot accent={accent} onDone={bootDone} />}
        {booted && locked && (
          <LockScreen profile={profile} accent={accent} sfx={sfx}
            unread={demo ? DEMO_HOME.unread : (home?.unread || 0)}
            mailUnread={demo ? DEMO_HOME.mail_unread : (home?.mail_unread || 0)}
            onUnlock={() => setLocked(false)} />
        )}

        {/* notification shade */}
        {shade && booted && !locked && (
          <NotificationShade accent={accent}
            unread={demo ? 0 : (home?.unread || 0)}
            mailUnread={demo ? DEMO_HOME.mail_unread : (home?.mail_unread || 0)}
            dealer101={demo ? null : dealState}
            spam={spam}
            onOpenApp={(id) => { setShade(false); openApp(id); }}
            onDismissSpam={(id) => setSpam((s) => s.filter((x) => x.id !== id))}
            onClearSpam={() => setSpam([])}
            onClose={() => setShade(false)} />
        )}

        {/* transient banner for incoming spam/wrong-number texts */}
        {banner && !shade && !locked && (
          <button data-testid="spam-banner" onClick={() => { setBanner(null); setShade(true); }}
            className="absolute top-8 inset-x-3 z-30 rounded-2xl bg-zinc-900/95 border border-white/15 backdrop-blur-md p-3 flex items-start gap-3 text-left shadow-2xl"
            style={{ animation: "phBannerIn 0.25s ease both" }}>
            <span className="w-8 h-8 rounded-full bg-orange-400/15 border border-orange-400/30 flex items-center justify-center shrink-0">
              <MessageSquare className="w-3.5 h-3.5 text-orange-300" />
            </span>
            <span className="flex-1 min-w-0">
              <span className="block font-mono text-[8px] uppercase tracking-[0.2em] text-white/50">{banner.kind === "wrong" ? "Unknown number" : "Message · probably spam"}</span>
              <span className="block font-mono text-[9px] text-white/80 truncate">{banner.body}</span>
            </span>
          </button>
        )}

        {/* scam-likely incoming call */}
        {scamCall && (
          <div data-testid="scam-call" className="absolute inset-0 z-30 bg-black/85 backdrop-blur-md flex flex-col items-center justify-center gap-4" style={{ animation: "ncFadeIn 0.25s ease both" }}>
            <span className="w-20 h-20 rounded-full bg-white/10 flex items-center justify-center animate-pulse"><PhoneIncoming className="w-9 h-9 text-orange-300" /></span>
            <p className="font-sans font-bold text-2xl text-white">Unknown</p>
            <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-red-400 font-bold">scam likely</p>
            <div className="flex gap-8 mt-4">
              <button data-testid="scam-decline" onClick={() => {
                sfx.stopRing(); sfx.keyTick();
                pushSpam({ id: scamCall.id, kind: "missed", body: "Missed call · Unknown — Bornwood Telecom flagged this number as scam likely." });
                setScamCall(null);
              }} className="w-16 h-16 rounded-full bg-red-500 hover:bg-red-400 flex flex-col items-center justify-center text-white transition-colors"><PhoneOff className="w-6 h-6" /></button>
              <button data-testid="scam-answer" onClick={answerScam} className="w-16 h-16 rounded-full bg-emerald-500 hover:bg-emerald-400 flex items-center justify-center text-white transition-colors"><Phone className="w-6 h-6" /></button>
            </div>
          </div>
        )}

        {/* active scam call — the gamble: hang up fast or get drained */}
        {activeScam && (
          <div data-testid="scam-active" className="absolute inset-0 z-30 bg-black/85 backdrop-blur-md flex flex-col items-center justify-center gap-4 px-8" style={{ animation: "ncFadeIn 0.25s ease both" }}>
            <span className="w-16 h-16 rounded-full bg-red-500/15 border border-red-400/30 flex items-center justify-center"><PhoneIncoming className="w-7 h-7 text-red-300 animate-pulse" /></span>
            <div className="text-center">
              <p className="font-sans font-bold text-xl text-white">Unknown</p>
              <p data-testid="scam-elapsed" className="font-mono text-[9px] uppercase tracking-[0.25em] text-red-300 mt-1">
                connected · {Math.max(0, Math.floor((now.getTime() - activeScam.at) / 1000))}s
              </p>
            </div>
            <p className="font-mono text-[10px] text-white/60 italic text-center leading-relaxed">"{activeScam.line}"</p>
            <button data-testid="scam-hangup" onClick={() => resolveRef.current?.(activeScam)}
              className="w-16 h-16 rounded-full bg-red-500 hover:bg-red-400 flex flex-col items-center justify-center text-white transition-colors animate-pulse"><PhoneOff className="w-6 h-6" /></button>
            <p className="font-mono text-[8px] uppercase tracking-[0.2em] text-white/35 text-center">the longer you listen, the worse it gets</p>
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------------- first-run setup wizard ---------------- */
function SetupWizard({ accent: initial, sfx, onDone }) {
  const { t } = useI18n();
  const [step, setStep] = useState(0); // 0 welcome 1 wallpaper 2 accent 3 ringtone 4 activating
  const [wp, setWp] = useState("midnight");
  const [accent, setAccent] = useState(initial);
  const [ring, setRing] = useState("pulse");
  const [busy, setBusy] = useState(false);

  const finish = async () => {
    setStep(4);
    sfx.bootChime();
    setBusy(true);
    await onDone({ wallpaper: wp, accent, notifications: true, ringtone: ring });
    setBusy(false);
  };

  const dots = <div className="flex gap-1.5 justify-center mt-2">{[0, 1, 2, 3].map((i) => <span key={i} className="w-1.5 h-1.5 rounded-full transition-colors" style={{ backgroundColor: step >= i ? accent : "rgba(255,255,255,0.15)" }} />)}</div>;

  return (
    <div data-testid="setup-wizard" className="flex-1 flex flex-col rounded-[2rem] bg-black/70 backdrop-blur-xl border border-white/10 p-5 overflow-y-auto" style={{ scrollbarWidth: "thin" }}>
      {step === 0 && (
        <div className="flex-1 flex flex-col items-center justify-center text-center gap-3">
          <span className="w-16 h-16 rounded-3xl flex items-center justify-center border border-white/15 bg-white/5"><Phone className="w-7 h-7" style={{ color: accent }} /></span>
          <p className="font-sans font-bold text-2xl text-white">{t("ph.welcome")}</p>
          <p className="font-mono text-[10px] text-white/60 leading-relaxed">Speccom Wireless is activating your line…<br />Messages, calls, Buzzer, the market and City Hall — one tap away.</p>
          <button data-testid="setup-start" onClick={() => { sfx.appOpen(); setStep(1); }} className="mt-4 px-8 py-3.5 rounded-2xl font-mono text-[11px] uppercase tracking-[0.25em] font-bold text-black" style={{ backgroundColor: accent }}>{t("ph.getstarted")}</button>
        </div>
      )}
      {step === 1 && (
        <>
          <p className="font-sans font-bold text-xl text-white">Pick a wallpaper</p>
          <p className="font-mono text-[10px] text-white/50 mb-3">Your home screen, your vibe.</p>
          <div className="grid grid-cols-4 gap-2">
            {Object.entries(WALLPAPERS).map(([k, v]) => (
              <button key={k} data-testid={`setup-wp-${k}`} onClick={() => { sfx.keyTick(); setWp(k); }} className={`h-16 rounded-xl border-2 transition-all ${wp === k ? "scale-105" : "border-white/10 opacity-70 hover:opacity-100"}`} style={{ background: v.css, borderColor: wp === k ? accent : undefined }} title={v.label} />
            ))}
          </div>
          <div className="mt-auto pt-4"><button data-testid="setup-next-1" onClick={() => { sfx.appOpen(); setStep(2); }} className="w-full py-3.5 rounded-2xl font-mono text-[11px] uppercase tracking-[0.25em] font-bold text-black" style={{ backgroundColor: accent }}>Next</button>{dots}</div>
        </>
      )}
      {step === 2 && (
        <>
          <p className="font-sans font-bold text-xl text-white">Choose an accent</p>
          <p className="font-mono text-[10px] text-white/50 mb-3">Used across buttons, icons and highlights.</p>
          <div className="flex gap-3 flex-wrap">
            {ACCENTS.map((c) => (
              <button key={c} data-testid={`setup-accent-${c.slice(1)}`} onClick={() => { sfx.keyTick(); setAccent(c); }} className={`w-11 h-11 rounded-full border-2 transition-transform ${accent === c ? "scale-110 border-white" : "border-transparent"}`} style={{ backgroundColor: c }} />
            ))}
          </div>
          <div className="mt-auto pt-4"><button data-testid="setup-next-2" onClick={() => { sfx.appOpen(); setStep(3); }} className="w-full py-3.5 rounded-2xl font-mono text-[11px] uppercase tracking-[0.25em] font-bold text-black" style={{ backgroundColor: accent }}>Next</button>{dots}</div>
        </>
      )}
      {step === 3 && (
        <>
          <p className="font-sans font-bold text-xl text-white">Pick a ringtone</p>
          <p className="font-mono text-[10px] text-white/50 mb-3">Tap to preview — this plays when citizens call you.</p>
          <div className="flex flex-col gap-2">
            {RINGTONES.map(([k, label]) => (
              <button key={k} data-testid={`setup-ring-${k}`} onClick={() => { setRing(k); sfx.playRing(k); }} className={`flex items-center gap-3 border rounded-xl px-4 py-3.5 transition-colors ${ring === k ? "bg-white/10" : "border-white/10 hover:bg-white/5"}`} style={{ borderColor: ring === k ? accent : undefined }}>
                <PhoneIncoming className="w-4 h-4" style={{ color: accent }} />
                <span className="font-mono text-[11px] text-white">{label}</span>
                {ring === k && <span className="ml-auto font-mono text-[8px] uppercase tracking-[0.2em]" style={{ color: accent }}>Selected</span>}
              </button>
            ))}
          </div>
          <div className="mt-auto pt-4"><button data-testid="setup-finish" disabled={busy} onClick={finish} className="w-full py-3.5 rounded-2xl font-mono text-[11px] uppercase tracking-[0.25em] font-bold text-black flex items-center justify-center gap-2" style={{ backgroundColor: accent }}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Activate my phone"}</button>{dots}</div>
        </>
      )}
      {step === 4 && (
        <div className="flex-1 flex flex-col items-center justify-center gap-4 text-center">
          <Loader2 className="w-8 h-8 animate-spin" style={{ color: accent }} />
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/70">Activating with Speccom Wireless…</p>
          <div className="w-48 h-1 rounded-full bg-white/10 overflow-hidden"><div className="h-full rounded-full" style={{ backgroundColor: accent, animation: "ncIntroProgress 2.4s ease both", width: "100%" }} /></div>
        </div>
      )}
    </div>
  );
}

/* ---------------- 911 — full voice call with the AI dispatcher ---------------- */
function SosCall({ demo, serverId, token, me, sfx, onClose }) {
  const [phase, setPhase] = useState("dialing"); // dialing | live | dispatched | ended
  const [callId, setCallId] = useState("");
  const [lines, setLines] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [services, setServices] = useState([]);
  const [recording, setRecording] = useState(false);
  const [speaking, setSpeaking] = useState(false); // dispatcher voice playing
  const [micNote, setMicNote] = useState("");
  const [recSecs, setRecSecs] = useState(0);
  const audioRef = useRef(null);
  const scrollRef = useRef(null);
  const recRef = useRef(null);
  const chunksRef = useRef([]);

  useEffect(() => {
    sfx.dialTone();
    const t1 = setTimeout(() => sfx.ringback(), 1100);
    const t2 = setTimeout(async () => {
      if (demo) {
        setPhase("live");
        setLines([{ role: "dispatcher", text: "911, what's your emergency?" }]);
        return;
      }
      try {
        const r = await api.post(`/nc/servers/${serverId}/phone/911/start`, {}, { params: { token } });
        setCallId(r.data.call_id);
        setPhase("live");
        setLines([{ role: "dispatcher", text: r.data.reply.text, audio_url: r.data.reply.audio_url }]);
      } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setPhase("ended"); }
    }, 2600);
    return () => { clearTimeout(t1); clearTimeout(t2); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // autoplay dispatcher voice + speaking indicator
  useEffect(() => {
    const last = lines[lines.length - 1];
    if (last?.role === "dispatcher" && last.audio_url && audioRef.current) {
      audioRef.current.src = last.audio_url;
      audioRef.current.play().catch(() => setSpeaking(false));
    }
    scrollRef.current?.scrollTo({ top: 99999 });
  }, [lines]);

  useEffect(() => {
    if (!recording) return;
    const t = setInterval(() => setRecSecs((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [recording]);

  /* ---- push-to-talk ---- */
  const startRec = async () => {
    if (busy || phase !== "live") return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      recRef.current = rec;
      rec.start();
      setRecSecs(0);
      setRecording(true);
      sfx.keyTick();
    } catch {
      setMicNote("Mic unavailable here — type below instead");
    }
  };

  const stopRec = async (sendIt) => {
    const rec = recRef.current;
    if (!rec || rec.state === "inactive") return;
    setRecording(false);
    await new Promise((res) => { rec.onstop = res; rec.stop(); });
    rec.stream.getTracks().forEach((t) => t.stop());
    if (!sendIt) { chunksRef.current = []; return; }
    const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
    chunksRef.current = [];
    if (blob.size < 500) { setMicNote("Too short — hold a moment and speak"); return; }
    if (demo) { demoVoiceTurn(); return; }
    setBusy(true);
    setLines((l) => [...l, { role: "caller", text: "…", voice: true, pending: true }]);
    try {
      const fd = new FormData();
      fd.append("file", blob, "voice.webm");
      const r = await api.post(`/nc/servers/${serverId}/phone/911/${callId}/say_voice`, fd, { params: { token } });
      setLines((l) => [...l.filter((x) => !x.pending), { role: "caller", text: r.data.heard, voice: true }, { role: "dispatcher", text: r.data.reply.text, audio_url: r.data.reply.audio_url }]);
      if (r.data.dispatched) { setPhase("dispatched"); setServices(r.data.services || []); sfx.dispatchChirp(); }
    } catch (e) {
      setLines((l) => l.filter((x) => !x.pending));
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const demoVoiceTurn = () => {
    setBusy(true);
    setLines((l) => [...l, { role: "caller", text: "(voice) fire at the mall on 5th street!", voice: true }]);
    setTimeout(() => {
      setLines((l) => [...l, { role: "dispatcher", text: "Fire crews are rolling — get clear of the building. (preview)" }]);
      setServices(["fire"]); setPhase("dispatched"); sfx.dispatchChirp(); setBusy(false);
    }, 1100);
  };

  const say = async () => {
    if (!input.trim()) return;
    const text = input.trim();
    setInput("");
    setLines((l) => [...l, { role: "caller", text }]);
    setBusy(true);
    if (demo) {
      setTimeout(() => {
        const lower = text.toLowerCase();
        const hasWhere = /street|st\b|ave|road|rd\b|blvd|downtown|square|station|mall|bank|hospital/i.test(text);
        if (/fire|burning|smoke/.test(lower) || hasWhere || lines.length > 4) {
          setLines((l) => [...l, { role: "dispatcher", text: "Units are en route to you now. Stay on the line if you can. (preview)" }]);
          setServices(["police", "ems"]); setPhase("dispatched"); sfx.dispatchChirp();
        } else {
          setLines((l) => [...l, { role: "dispatcher", text: "Copy. Where are you right now — street or nearest landmark? (preview)" }]);
        }
        setBusy(false);
      }, 900);
      return;
    }
    try {
      const r = await api.post(`/nc/servers/${serverId}/phone/911/${callId}/say`, { text }, { params: { token } });
      setLines((l) => [...l, { role: "dispatcher", text: r.data.reply.text, audio_url: r.data.reply.audio_url }]);
      if (r.data.dispatched) { setPhase("dispatched"); setServices(r.data.services || []); sfx.dispatchChirp(); }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const hangUp = async () => {
    if (recording) await stopRec(false);
    if (!demo && callId) { try { await api.post(`/nc/servers/${serverId}/phone/911/${callId}/end`, {}, { params: { token } }); } catch {} }
    setPhase("ended");
    setTimeout(onClose, 800);
  };

  const statusLine = phase === "dialing" ? "Connecting to dispatch…"
    : phase === "ended" ? "Call ended"
    : phase === "dispatched" ? "Units dispatched — stay on the line"
    : recording ? `Listening… 0:${String(recSecs).padStart(2, "0")} — tap the mic to send`
    : speaking ? "Dispatch speaking…"
    : busy ? "Dispatch is responding…"
    : "Your turn — tap the mic and speak";

  return (
    <div data-testid="sos-call" className="flex-1 flex flex-col rounded-[2rem] bg-[#160507]/95 backdrop-blur-xl border border-red-500/30 overflow-hidden" style={{ animation: "ncFadeIn 0.3s ease both" }}>
      <div className="px-4 py-3 border-b border-red-500/20 flex items-center gap-3">
        <span className={`w-2.5 h-2.5 rounded-full bg-red-500 ${phase !== "ended" ? "animate-pulse" : ""}`} />
        <div className="flex-1">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-red-300">911 Emergency</p>
          <p data-testid="sos-phase" className="font-mono text-[8px] text-white/50 uppercase tracking-[0.2em]">{phase === "dialing" ? "Connecting…" : phase === "dispatched" ? "Units dispatched" : phase}</p>
        </div>
        {phase === "dispatched" && <span className="font-mono text-[8px] uppercase border border-emerald-500/50 text-emerald-300 px-2 py-1">{(services || []).join(" + ") || "units"} en route</span>}
      </div>

      {/* voice-first stage: live waveform-style orb + captions */}
      <div className="px-4 pt-4 flex flex-col items-center gap-2">
        <button data-testid="sos-mic" disabled={phase !== "live" || busy}
          onClick={() => (recording ? stopRec(true) : startRec())}
          className={`w-20 h-20 rounded-full flex items-center justify-center border-4 transition-all disabled:opacity-40 ${recording ? "border-red-400 bg-red-500/30 scale-110" : "border-white/20 bg-white/5 hover:border-red-400/60"}`}
          style={recording ? { boxShadow: "0 0 44px rgba(239,68,68,0.45)" } : {}}>
          {busy ? <Loader2 className="w-7 h-7 animate-spin text-white" /> : recording ? <Send className="w-7 h-7 text-red-200" /> : <Mic className="w-7 h-7 text-white" />}
        </button>
        {recording && (
          <div className="flex items-end gap-1 h-5" data-testid="sos-waveform">
            {[0.5, 1, 0.7, 1.2, 0.6, 0.9, 0.5].map((h, i) => (
              <span key={i} className="w-1 rounded bg-red-400" style={{ height: `${h * 100}%`, animation: `ncPulse ${0.5 + i * 0.09}s ease-in-out infinite alternate` }} />
            ))}
          </div>
        )}
        {speaking && !recording && <p className="font-mono text-[8px] uppercase tracking-[0.25em] text-red-300 animate-pulse">♪ dispatch on air</p>}
        <p data-testid="sos-status" className="font-mono text-[9px] text-white/60 text-center">{statusLine}</p>
        {micNote && <p className="font-mono text-[8px] text-amber-300">{micNote}</p>}
      </div>

      {/* captions / transcript */}
      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto px-4 py-3 flex flex-col gap-2" style={{ scrollbarWidth: "thin" }}>
        {phase === "dialing" && <p className="font-mono text-[10px] text-white/50 text-center mt-6 animate-pulse">Calling emergency services…</p>}
        {lines.map((l, i) => (
          <div key={i} className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl font-mono text-[11px] leading-relaxed ${l.role === "caller" ? "self-end bg-red-600/80 text-white rounded-br-sm" : "self-start bg-white/10 text-white rounded-bl-sm border border-white/10"}`}>
            {l.role === "dispatcher" && <span className="block text-[7.5px] uppercase tracking-[0.2em] text-red-300/80 mb-0.5">Dispatch</span>}
            {l.text}
            {l.voice && <span className="block text-[7.5px] text-white/50 mt-0.5">🎙 voice</span>}
            {l.audio_url && <span className="block text-[7.5px] text-white/40 mt-0.5">♪ voice</span>}
          </div>
        ))}
      </div>

      {phase !== "ended" && (
        <div className="p-3 border-t border-red-500/20 flex flex-col gap-2">
          <div className="flex gap-2">
            <input data-testid="sos-input" value={input} maxLength={400} disabled={phase !== "live" || busy || recording}
              onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && say()}
              placeholder="…or type to the dispatcher"
              className="flex-1 bg-white/10 border border-white/15 rounded-full px-4 py-2.5 font-mono text-[11px] text-white focus:outline-none disabled:opacity-40" />
            <button data-testid="sos-send" onClick={say} disabled={busy || !input.trim() || phase !== "live"} className="w-10 h-10 rounded-full bg-red-500 flex items-center justify-center text-white disabled:opacity-40">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </div>
          <div className="flex gap-2">
            {recording && <button data-testid="sos-mic-cancel" onClick={() => stopRec(false)} className="flex-1 py-2.5 rounded-xl font-mono text-[9px] uppercase tracking-[0.25em] border border-white/20 text-white/60 hover:bg-white/5 transition-colors">Discard</button>}
            <button data-testid="sos-end" onClick={hangUp} className="flex-1 py-2.5 rounded-xl font-mono text-[9px] uppercase tracking-[0.25em] border border-red-500/40 text-red-300 hover:bg-red-500/10 transition-colors flex items-center justify-center gap-2"><PhoneOff className="w-3.5 h-3.5" /> End call</button>
          </div>
        </div>
      )}
      <audio ref={audioRef} className="hidden" onPlay={() => setSpeaking(true)} onEnded={() => setSpeaking(false)} onError={() => setSpeaking(false)} />
    </div>
  );
}

/* ---------------- Buzzer (in-universe social) ---------------- */
const DEMO_TRENDS = [{ tag: "NCElection", posts: 14 }, { tag: "NightMarket", posts: 9 }, { tag: "Lynx", posts: 5 }, { tag: "HarborPD", posts: 3 }];
const DEMO_REPLIES = [
  { id: "r1", author_name: "Ray Dalton", handle: "ray_dalton", body: "taxis are standing by for voters, rides half price today", likes: 6, liked: false, replies: 0, at: iso(30), image_url: "" },
  { id: "r2", author_name: "Mia Torres", handle: "mia_torres", body: "this is why the night market matters — turnout!", likes: 4, liked: false, replies: 0, at: iso(20), image_url: "" },
];

function BuzzerApp({ demo, serverId, token, me, accent, sfx, onProfile }) {
  const [feed, setFeed] = useState([]);
  const [trending, setTrending] = useState([]);
  const [tag, setTag] = useState("");
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [thread, setThread] = useState(null); // {post, replies}
  const [replyDraft, setReplyDraft] = useState("");
  const SKY = "#38bdf8";

  const load = useCallback(() => {
    if (demo) { setFeed((f) => f.length ? f : DEMO_BLEETS); setTrending(DEMO_TRENDS); return; }
    api.get(`/nc/servers/${serverId}/buzzer/feed`, { params: { token, tag } }).then((r) => setFeed(r.data.feed)).catch(() => {});
    api.get(`/nc/servers/${serverId}/buzzer/trending`).then((r) => setTrending(r.data.trending)).catch(() => {});
  }, [demo, serverId, token, tag]);
  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, [load]);

  const openThread = (p) => {
    sfx.appOpen();
    if (demo) { setThread({ post: p, replies: DEMO_REPLIES }); return; }
    api.get(`/nc/servers/${serverId}/buzzer/posts/${p.id}`, { params: { token } }).then((r) => setThread(r.data)).catch(() => {});
  };

  const post = async () => {
    if (!draft.trim()) return;
    setBusy(true);
    if (demo) {
      setFeed((f) => [{ id: `l${Date.now()}`, author_name: me.name, handle: "you", body: draft.trim(), likes: 0, liked: false, replies: 0, at: new Date().toISOString(), image_url: "" }, ...f]);
      setDraft(""); setBusy(false); sfx.sendSwoosh();
      return;
    }
    try {
      await api.post(`/nc/servers/${serverId}/buzzer/posts`, { body: draft.trim() }, { params: { token } });
      setDraft(""); sfx.sendSwoosh(); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const reply = async () => {
    if (!replyDraft.trim()) return;
    setBusy(true);
    if (demo) {
      setThread((t) => ({ ...t, replies: [...t.replies, { id: `l${Date.now()}`, author_name: me.name, handle: "you", body: replyDraft.trim(), likes: 0, liked: false, replies: 0, at: new Date().toISOString(), image_url: "" }] }));
      setReplyDraft(""); setBusy(false); sfx.sendSwoosh();
      return;
    }
    try {
      await api.post(`/nc/servers/${serverId}/buzzer/posts/${thread.post.id}/reply`, { body: replyDraft.trim() }, { params: { token } });
      setReplyDraft(""); sfx.sendSwoosh(); openThread(thread.post); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const like = async (p, inThread = false) => {
    const flip = (x) => x.id === p.id ? { ...x, liked: !x.liked, likes: x.likes + (x.liked ? -1 : 1) } : x;
    if (demo) { inThread ? setThread((t) => ({ ...t, post: flip(t.post), replies: t.replies.map(flip) })) : setFeed((f) => f.map(flip)); sfx.keyTick(); return; }
    try { await api.post(`/nc/servers/${serverId}/buzzer/posts/${p.id}/like`, {}, { params: { token } }); sfx.keyTick(); inThread ? openThread(thread.post) : load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const BleetRow = ({ p, inThread = false }) => (
    <div data-testid={`bleet-${p.id.slice(0, 6)}`} className="rounded-2xl bg-white/5 border border-white/10 p-3.5 flex gap-3 w-full text-left">
      <span className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center shrink-0 font-sans font-bold text-sm" style={{ color: SKY }}>{(p.author_name || "C")[0]}</span>
      <div className="flex-1 min-w-0">
        <p className="font-mono text-[10px] text-white/90">
          <button data-testid={`bleet-author-${p.id.slice(0, 6)}`} onClick={() => { if (p.author_pc && onProfile) { sfx?.keyTick(); onProfile({ pc: p.author_pc, name: p.author_name }); } }}
            className="font-bold text-white hover:underline underline-offset-2">{p.author_name}</button>
          <span className="text-white/40"> @{p.handle} · {String(p.at).slice(11, 16)}</span>
        </p>
        <p className="font-mono text-[11px] text-white/80 leading-relaxed mt-1 break-words">
          {(p.body || "").split(/(#\w+)/g).map((part, i) => part.startsWith("#")
            ? <button key={i} onClick={() => { setTag(part.slice(1)); setThread(null); }} className="font-bold hover:underline" style={{ color: SKY }}>{part}</button>
            : <span key={i}>{part}</span>)}
        </p>
        {p.image_url && <img src={p.image_url} alt="" className="rounded-xl mt-2 max-w-full border border-white/10" />}
        <div className="mt-2 flex items-center gap-4">
          <button data-testid={`bleet-like-${p.id.slice(0, 6)}`} onClick={() => like(p, inThread)} className={`flex items-center gap-1.5 font-mono text-[10px] transition-colors ${p.liked ? "text-red-400" : "text-white/40 hover:text-red-300"}`}>
            <Heart className={`w-3.5 h-3.5 ${p.liked ? "fill-red-400" : ""}`} /> {p.likes}
          </button>
          {!inThread && (
            <button data-testid={`bleet-reply-${p.id.slice(0, 6)}`} onClick={() => openThread(p)} className="flex items-center gap-1.5 font-mono text-[10px] text-white/40 hover:text-sky-300 transition-colors">
              <MessageSquare className="w-3.5 h-3.5" /> {p.replies || 0}
            </button>
          )}
        </div>
      </div>
    </div>
  );

  /* thread view */
  if (thread) {
    return (
      <div className="p-3 flex flex-col gap-2">
        <button data-testid="bleet-thread-back" onClick={() => { sfx.back(); setThread(null); load(); }} className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.2em] text-white/60 hover:text-white transition-colors w-fit"><ChevronLeft className="w-4 h-4" /> Timeline</button>
        <BleetRow p={thread.post} inThread />
        <div className="ml-4 pl-3 border-l-2 border-white/10 flex flex-col gap-2">
          {thread.replies.map((r) => <BleetRow key={r.id} p={r} inThread />)}
          {thread.replies.length === 0 && <p className="font-mono text-[9px] text-white/40 py-2">No replies yet — start the drama.</p>}
        </div>
        <div className="flex gap-2 mt-1">
          <input data-testid="reply-input" value={replyDraft} maxLength={280} onChange={(e) => setReplyDraft(e.target.value)} onKeyDown={(e) => e.key === "Enter" && reply()} placeholder={`Reply to @${thread.post.handle}…`}
            className="flex-1 bg-white/10 border border-white/15 rounded-full px-4 py-2.5 font-mono text-[11px] text-white focus:outline-none" />
          <button data-testid="reply-send" onClick={reply} disabled={busy || !replyDraft.trim()} className="w-10 h-10 rounded-full flex items-center justify-center text-black disabled:opacity-40" style={{ backgroundColor: SKY }}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 flex flex-col gap-2">
      {/* trending rail */}
      {trending.length > 0 && (
        <div className="flex gap-1.5 overflow-x-auto pb-1" style={{ scrollbarWidth: "none" }}>
          {tag && <button data-testid="trend-clear" onClick={() => setTag("")} className="shrink-0 font-mono text-[9px] uppercase border border-white/20 text-white/70 px-2.5 py-1.5 rounded-full">✕ #{tag}</button>}
          {trending.map((t) => (
            <button key={t.tag} data-testid={`trend-${t.tag}`} onClick={() => setTag(tag === t.tag ? "" : t.tag)}
              className={`shrink-0 font-mono text-[9px] px-2.5 py-1.5 rounded-full border transition-colors ${tag === t.tag ? "text-black" : "border-white/15 text-white/70 hover:text-white"}`}
              style={tag === t.tag ? { backgroundColor: SKY, borderColor: SKY } : {}}>
              #{t.tag} <span className="opacity-60">{t.posts}</span>
            </button>
          ))}
        </div>
      )}
      {tag && <p className="font-mono text-[8px] uppercase tracking-[0.25em] text-white/40 px-1">Showing bleets tagged #{tag}</p>}
      <div className="rounded-2xl bg-white/5 border border-white/10 p-3 flex flex-col gap-2">
        <textarea data-testid="bleet-input" value={draft} maxLength={280} onChange={(e) => setDraft(e.target.value)} rows={2} placeholder="What's happening in the city?"
          className="w-full bg-transparent font-mono text-[11px] text-white placeholder:text-white/30 focus:outline-none resize-none" />
        <div className="flex items-center justify-between">
          <span className="font-mono text-[8px] text-white/40">{280 - draft.length}</span>
          <button data-testid="bleet-post" onClick={post} disabled={busy || !draft.trim()} className="px-5 py-2 rounded-full font-mono text-[10px] uppercase tracking-[0.2em] font-bold text-black disabled:opacity-40" style={{ backgroundColor: SKY }}>Bleet</button>
        </div>
      </div>
      {feed.map((p) => <BleetRow key={p.id} p={p} />)}
      {feed.length === 0 && <p className="font-mono text-[10px] text-white/40 text-center mt-8">{tag ? `No bleets under #${tag} yet.` : "Quiet timeline — post the first bleet."}</p>}
    </div>
  );
}

/* ---------------- Market ---------------- */
function Spark({ history, up, accent }) {
  if (!history || history.length < 2) return null;
  const min = Math.min(...history), max = Math.max(...history);
  const w = 64, h = 22;
  const pts = history.map((v, i) => `${(i / (history.length - 1)) * w},${h - ((v - min) / ((max - min) || 1)) * h}`).join(" ");
  return (
    <svg width={w} height={h} className="shrink-0"><polyline points={pts} fill="none" stroke={up ? "#22c55e" : "#ef4444"} strokeWidth="1.5" /></svg>
  );
}

function MarketApp({ demo, serverId, token, accent }) {
  const [mkt, setMkt] = useState(null);
  const [port, setPort] = useState(null);
  const [tab, setTab] = useState("goods");
  const [trading, setTrading] = useState(null); // instrument object
  const [qty, setQty] = useState(1);
  const [busy, setBusy] = useState(false);
  const AMBER = "#f59e0b";

  const load = useCallback(() => {
    if (demo) {
      setMkt(DEMO_MARKET);
      setPort({ bank: 12450, holdings: [{ key: "gold", name: "Gold", kind: "goods", qty: 2, price: 1241.5, value: 2483 }], holdings_value: 2483 });
      return;
    }
    api.get(`/nc/servers/${serverId}/market`).then((r) => setMkt(r.data)).catch(() => {});
    api.get(`/nc/servers/${serverId}/market/portfolio`, { params: { token } }).then((r) => setPort(r.data)).catch(() => {});
  }, [demo, serverId, token]);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  const trade = async (side) => {
    if (!trading) return;
    if (demo) { toast.success(`Preview: ${side} ${qty} × ${trading.name} — in-game this moves real money and the price`); return; }
    setBusy(true);
    try {
      const r = await api.post(`/nc/servers/${serverId}/market/trade`, { key: trading.key, side, qty }, { params: { token } });
      toast.success(r.data.message);
      sfxTradeOk();
      setTrading(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const sfxTradeOk = () => {};

  if (!mkt) return <Loader2 className="w-5 h-5 animate-spin mx-auto mt-10" style={{ color: accent }} />;
  const rows = tab === "goods" ? mkt.goods : mkt.stocks;
  const heldOf = (k) => (port?.holdings || []).find((h) => h.key === k)?.qty || 0;

  return (
    <div className="p-3 flex flex-col gap-2">
      {/* portfolio */}
      {port && (
        <div data-testid="market-portfolio" className="rounded-2xl border border-white/10 bg-white/5 p-3.5 flex items-center gap-4">
          <div><p className="font-mono text-[8px] uppercase tracking-[0.2em] text-white/50">Bank</p><p className="font-sans font-bold text-lg text-white">${(port.bank || 0).toLocaleString()}</p></div>
          <div className="w-px h-8 bg-white/10" />
          <div><p className="font-mono text-[8px] uppercase tracking-[0.2em] text-white/50">Holdings</p><p className="font-sans font-bold text-lg" style={{ color: AMBER }}>${(port.holdings_value || 0).toLocaleString()}</p></div>
          <div className="ml-auto text-right"><p className="font-mono text-[8px] uppercase tracking-[0.2em] text-white/50">Positions</p><p className="font-sans font-bold text-lg text-white">{(port.holdings || []).length}</p></div>
        </div>
      )}
      <div className="flex gap-1.5">
        {[["goods", "Goods"], ["stocks", "Stocks"]].map(([id, l]) => (
          <button key={id} data-testid={`market-tab-${id}`} onClick={() => setTab(id)} className={`flex-1 py-2 rounded-xl font-mono text-[9px] uppercase tracking-[0.2em] border transition-colors ${tab === id ? "text-black" : "border-white/15 text-white/60"}`} style={tab === id ? { backgroundColor: AMBER, borderColor: AMBER } : {}}>{l}</button>
        ))}
      </div>

      {rows.map((g) => {
        const up = g.change >= 0;
        const held = heldOf(g.key);
        return (
          <button key={g.key} data-testid={`market-${g.key}`} onClick={() => { setTrading(g); setQty(1); }} className="w-full text-left flex items-center gap-3 rounded-xl bg-white/5 border border-white/10 px-3.5 py-3 hover:border-white/25 transition-colors">
            <div className="flex-1 min-w-0">
              <p className="font-sans font-semibold text-sm text-white truncate">{g.name}{held > 0 && <span className="ml-1.5 font-mono text-[8px] px-1.5 py-0.5 rounded-full border" style={{ color: AMBER, borderColor: `${AMBER}66` }}>held ×{held}</span>}</p>
              <p className="font-mono text-[8px] uppercase text-white/40">{g.key}{tab === "goods" ? " / unit" : ""}</p>
            </div>
            <Spark history={g.history} up={up} />
            <div className="text-right w-20">
              <p className="font-mono text-[12px] font-bold text-white">${g.price.toLocaleString()}</p>
              <p className={`font-mono text-[9px] flex items-center justify-end gap-0.5 ${up ? "text-emerald-400" : "text-red-400"}`}>
                {up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}{up ? "+" : ""}{g.change}%
              </p>
            </div>
          </button>
        );
      })}
      <p className="font-mono text-[8px] text-white/40 leading-relaxed px-1">Tap an instrument to trade with your bank balance — your buys and sells move the price. City sales tax: {mkt.tax_sales}%.</p>

      {/* trade sheet */}
      {trading && (
        <div data-testid="trade-sheet" className="fixed inset-0 z-30 bg-black/70 backdrop-blur-sm flex items-end justify-center" onClick={() => setTrading(null)}>
          <div className="w-[392px] max-w-full rounded-t-3xl bg-zinc-950 border-t border-x border-white/15 p-5 flex flex-col gap-4" style={{ animation: "ncFadeUp 0.25s ease both" }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3">
              <div className="flex-1"><p className="font-sans font-bold text-lg text-white">{trading.name}</p><p className="font-mono text-[9px] text-white/40 uppercase">{trading.key} · held ×{heldOf(trading.key)}</p></div>
              <p className="font-mono font-bold text-xl text-white">${trading.price.toLocaleString()}</p>
            </div>
            <div className="flex items-center gap-2 justify-center">
              {[1, 5, 10, 25].map((n) => (
                <button key={n} data-testid={`qty-${n}`} onClick={() => setQty(n)} className={`px-4 py-2 rounded-xl font-mono text-[11px] border transition-colors ${qty === n ? "text-black" : "border-white/15 text-white/60"}`} style={qty === n ? { backgroundColor: AMBER, borderColor: AMBER } : {}}>{n}</button>
              ))}
            </div>
            <p className="font-mono text-[10px] text-white/60 text-center">Total: <span className="text-white font-bold">${(trading.price * qty).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span></p>
            <div className="flex gap-2">
              <button data-testid="trade-buy" disabled={busy} onClick={() => trade("buy")} className="flex-1 py-3.5 rounded-2xl bg-emerald-500 hover:bg-emerald-400 font-mono text-[11px] uppercase tracking-[0.2em] font-bold text-black transition-colors disabled:opacity-40 flex items-center justify-center gap-2">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Buy"}
              </button>
              <button data-testid="trade-sell" disabled={busy || heldOf(trading.key) < 1} onClick={() => trade("sell")} className="flex-1 py-3.5 rounded-2xl bg-red-500 hover:bg-red-400 font-mono text-[11px] uppercase tracking-[0.2em] font-bold text-white transition-colors disabled:opacity-40">Sell</button>
            </div>
            <p className="font-mono text-[8px] text-white/40 text-center">Settles from your bank instantly · heavy orders move the market</p>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------- bank ---------------- */
function BankApp({ demo, serverId, token, accent }) {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("accounts");
  const [to, setTo] = useState("");
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (demo) { setData(DEMO.phone); return; }
    api.get(`/nc/servers/${serverId}/bank/me`, { params: { token } }).then((r) => setData(r.data)).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  }, [demo, serverId, token]);
  useEffect(() => { load(); const t = setInterval(load, 12000); return () => clearInterval(t); }, [load]);

  const send = async () => {
    setBusy(true);
    try {
      if (demo) toast.success("Preview: transfer queued — in-game the bridge executes it");
      else { const r = await api.post(`/nc/servers/${serverId}/bank/transfer`, { to_playercode: Number(to), amount: Number(amount) }, { params: { token } }); toast.success(`Queued — ${r.data.status}`); setTo(""); setAmount(""); load(); }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  if (!data) return <Loader2 className="w-5 h-5 animate-spin mx-auto mt-10" style={{ color: accent }} />;
  return (
    <div className="p-4 flex flex-col gap-3">
      <div className="grid grid-cols-2 gap-2">
        <div className="rounded-2xl bg-white/5 border border-white/10 p-4"><p className="font-mono text-[8px] uppercase tracking-[0.25em] text-white/50">Bank</p><p data-testid="bank-balance" className="font-sans font-bold text-2xl text-white mt-1">${data.bank.toLocaleString()}</p></div>
        <div className="rounded-2xl bg-white/5 border border-white/10 p-4"><p className="font-mono text-[8px] uppercase tracking-[0.25em] text-white/50">Cash</p><p data-testid="cash-balance" className="font-sans font-bold text-2xl text-white mt-1">${data.cash.toLocaleString()}</p></div>
      </div>
      {data.isblocked && <p className="font-mono text-[9px] text-red-300 border border-red-500/40 bg-red-500/10 rounded-xl px-3 py-2">ACCOUNT FROZEN — contact a banker in character</p>}
      <div className="flex gap-1.5">
        {[["accounts", "Activity"], ["transfer", "Transfer"]].map(([id, l]) => (
          <button key={id} data-testid={`bank-tab-${id}`} onClick={() => setTab(id)} className={`flex-1 py-2 rounded-xl font-mono text-[9px] uppercase tracking-[0.2em] border transition-colors ${tab === id ? "text-black" : "border-white/15 text-white/60"}`} style={tab === id ? { backgroundColor: accent, borderColor: accent } : {}}>{l}</button>
        ))}
      </div>
      {tab === "accounts" && (
        <div className="flex flex-col gap-1.5">
          {(data.transactions || []).map((t) => (
            <div key={t.txid} className="flex items-center gap-2 rounded-xl bg-white/5 px-3 py-2.5">
              <span className="flex-1 font-mono text-[10px] text-white/80 truncate">{t.description}</span>
              <span className={`font-mono text-[11px] font-bold ${t.amount >= 0 ? "text-emerald-400" : "text-white/70"}`}>{t.amount >= 0 ? "+" : ""}{t.amount}</span>
            </div>
          ))}
          {(data.pending_transfers || []).map((p) => (
            <div key={p.id} className="flex items-center gap-2 rounded-xl border border-amber-500/30 bg-amber-500/5 px-3 py-2.5">
              <span className="flex-1 font-mono text-[10px] text-amber-200/80 truncate">Transfer to #{p.to_playercode} — processing in-game</span>
              <span className="font-mono text-[11px] text-amber-300">-{p.amount}</span>
            </div>
          ))}
        </div>
      )}
      {tab === "transfer" && (
        <div className="flex flex-col gap-2.5">
          <input data-testid="transfer-to" value={to} onChange={(e) => setTo(e.target.value.replace(/\D/g, ""))} placeholder="Citizen # (playercode)" className="bg-white/10 border border-white/15 rounded-xl px-4 py-3 font-mono text-[11px] text-white focus:outline-none" />
          <input data-testid="transfer-amount" value={amount} onChange={(e) => setAmount(e.target.value.replace(/\D/g, ""))} placeholder="Amount" className="bg-white/10 border border-white/15 rounded-xl px-4 py-3 font-mono text-[11px] text-white focus:outline-none" />
          <button data-testid="transfer-send" onClick={send} disabled={busy || !to || !amount} className="py-3 rounded-xl font-mono text-[10px] uppercase tracking-[0.25em] font-bold text-black disabled:opacity-40 flex items-center justify-center gap-2" style={{ backgroundColor: accent }}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send money"}
          </button>
          <p className="font-mono text-[8px] text-white/40">Transfers are executed in-game by the server bridge — no web exploit path.</p>
        </div>
      )}
    </div>
  );
}

/* ---------------- city hall (vote + mayor powers) ---------------- */
function CityHallApp({ demo, serverId, token, sfx }) {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState("");
  const [taxSales, setTaxSales] = useState(null);
  const [taxIncome, setTaxIncome] = useState(null);
  const [announce, setAnnounce] = useState("");
  const GOLD = "#e8b93e";
  const load = useCallback(() => {
    if (demo) { setSt({ ...DEMO.politics, you: { ...DEMO.politics.you, is_mayor: false }, announcements: [{ id: "a1", author: "Mayor R. O'Connor", text: "Night-market permits now fast-tracked through City Hall.", at: iso(300) }], economy: { tax_sales: 10, tax_income: 8 } }); return; }
    api.get(`/nc/servers/${serverId}/politics`, { params: { token } }).then((r) => setSt(r.data)).catch(() => {});
  }, [demo, serverId, token]);
  useEffect(() => { load(); const t = setInterval(load, 12000); return () => clearInterval(t); }, [load]);

  useEffect(() => {
    if (st?.economy && taxSales === null) { setTaxSales(st.economy.tax_sales ?? 10); setTaxIncome(st.economy.tax_income ?? 10); }
  }, [st, taxSales]);

  const vote = async (c) => {
    if (demo) { toast.info("Preview: your ballot counts in-game"); return; }
    setBusy(c.id);
    try { const r = await api.post(`/nc/servers/${serverId}/politics/vote`, { candidate_id: c.id }, { params: { token } }); toast.success(r.data.message); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(""); }
  };

  const mayorAct = async (payload) => {
    if (demo) { toast.info("Preview: seated mayors push this live to the economy"); return; }
    setBusy("mayor");
    try { const r = await api.post(`/nc/servers/${serverId}/mayor/action`, payload, { params: { token } }); toast.success(r.data.message); sfx.success(); setAnnounce(""); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(""); }
  };

  if (!st) return <Loader2 className="w-5 h-5 animate-spin mx-auto mt-10" style={{ color: GOLD }} />;
  const el = st.election;
  return (
    <div className="p-4 flex flex-col gap-3">
      {st.government && (
        <div className="rounded-2xl border p-4 flex items-center gap-3" style={{ borderColor: `${GOLD}44`, background: "rgba(232,185,62,0.06)" }}>
          <Landmark className="w-5 h-5" style={{ color: GOLD }} />
          <div><p className="font-mono text-[8px] uppercase tracking-[0.25em] text-white/50">{st.government.office} in office</p><p className="font-sans font-bold text-white">{st.government.name}</p></div>
        </div>
      )}

      {/* mayor's office */}
      {st.you?.is_mayor && (
        <div data-testid="mayor-office" className="rounded-2xl border p-4 flex flex-col gap-3" style={{ borderColor: `${GOLD}66`, background: "rgba(232,185,62,0.08)" }}>
          <p className="font-mono text-[9px] uppercase tracking-[0.3em]" style={{ color: GOLD }}>Mayor's office — live powers</p>
          {taxSales !== null && [
            ["tax_sales", "Sales tax", taxSales, setTaxSales],
            ["tax_income", "Income tax", taxIncome, setTaxIncome],
          ].map(([field, label, val, setter]) => (
            <div key={field}>
              <div className="flex justify-between font-mono text-[9px] text-white/60 mb-1"><span className="uppercase tracking-[0.2em]">{label}</span><span style={{ color: GOLD }}>{val}%</span></div>
              <input data-testid={`mayor-${field}`} type="range" min="0" max="30" step="1" value={val} onChange={(e) => setter(Number(e.target.value))}
                onMouseUp={() => mayorAct({ kind: "tax", field, value: val })} onTouchEnd={() => mayorAct({ kind: "tax", field, value: val })}
                className="w-full accent-[#e8b93e]" />
            </div>
          ))}
          <div className="flex gap-2">
            <input data-testid="mayor-announce" value={announce} maxLength={200} onChange={(e) => setAnnounce(e.target.value)} placeholder="Announce a policy to the city…"
              className="flex-1 bg-white/10 border border-white/15 rounded-xl px-3 py-2.5 font-mono text-[10px] text-white focus:outline-none" />
            <button data-testid="mayor-announce-send" disabled={busy === "mayor" || announce.trim().length < 5} onClick={() => mayorAct({ kind: "announce", text: announce.trim() })} className="px-4 rounded-xl font-mono text-[9px] uppercase font-bold text-black disabled:opacity-40" style={{ backgroundColor: GOLD }}>Post</button>
          </div>
        </div>
      )}

      {(st.announcements || []).length > 0 && (
        <div className="flex flex-col gap-1.5">
          <p className="font-mono text-[8px] uppercase tracking-[0.25em] text-white/50">City bulletins</p>
          {st.announcements.map((a) => (
            <div key={a.id} data-testid={`bulletin-${a.id.slice(0, 6)}`} className="rounded-xl bg-white/5 border border-white/10 px-3.5 py-2.5">
              <p className="font-mono text-[10px] text-white/85 leading-relaxed">{a.text}</p>
              <p className="font-mono text-[8px] text-white/40 mt-1">{a.author} · {String(a.at).slice(0, 10)}</p>
            </div>
          ))}
        </div>
      )}

      {!el && <p className="font-mono text-[10px] text-white/50 text-center mt-2">No election running.</p>}
      {el && (
        <>
          <p className="font-mono text-[9px] uppercase tracking-[0.25em]" style={{ color: GOLD }}>{el.office} election — {el.status === "voting" ? "voting open" : el.status === "registration" ? "registration open" : "closed"}</p>
          {st.candidates.map((c) => {
            const voted = st.you?.voted_for === c.id;
            return (
              <div key={c.id} data-testid={`ch-cand-${c.id.slice(0, 6)}`} className="rounded-xl bg-white/5 border border-white/10 p-3.5 flex flex-col gap-1.5">
                <div className="flex items-center gap-2"><p className="font-sans font-semibold text-sm text-white flex-1">{c.name}</p><span className="font-mono text-[10px]" style={{ color: GOLD }}>{c.votes} votes</span></div>
                <p className="font-mono text-[9.5px] text-white/60 leading-relaxed">{c.slogan || "No platform published."}</p>
                {el.status === "voting" && !voted && !st.you?.voted_for && (
                  <button data-testid={`ch-vote-${c.id.slice(0, 6)}`} disabled={busy === c.id} onClick={() => vote(c)} className="mt-1 py-2 rounded-lg font-mono text-[9px] uppercase tracking-[0.2em] font-bold text-black disabled:opacity-40 flex items-center justify-center gap-1.5" style={{ backgroundColor: GOLD }}>
                    {busy === c.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCheck className="w-3.5 h-3.5" />} Vote
                  </button>
                )}
                {voted && <p className="font-mono text-[8px] uppercase tracking-[0.2em]" style={{ color: GOLD }}>✓ Your vote</p>}
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}

/* ---------------- settings ---------------- */
function SettingsApp({ demo, profile, accent, sfx, ownedThemes = ["stock", "contrast"], onSave }) {
  const [customUrl, setCustomUrl] = useState(profile.wallpaper?.startsWith("http") || profile.wallpaper?.startsWith("/api/") ? profile.wallpaper : "");
  const [busy, setBusy] = useState(false);
  const [secSetup, setSecSetup] = useState(null); // {method, first, stage: enter|confirm}
  const [secEntry, setSecEntry] = useState("");
  const [secPat, setSecPat] = useState([]);
  const wp = profile.wallpaper;
  const acc = profile.accent;

  const save = async (patch) => { setBusy(true); await onSave(patch); setBusy(false); };

  const secAdvance = async (code) => {
    if (!secSetup) return;
    if (secSetup.stage === "enter") {
      setSecSetup({ ...secSetup, first: code, stage: "confirm" });
      setSecEntry(""); setSecPat([]);
      return;
    }
    if (code !== secSetup.first) {
      toast.error("Didn't match — try again");
      setSecSetup({ ...secSetup, first: null, stage: "enter" });
      setSecEntry(""); setSecPat([]);
      return;
    }
    const ok = await onSave({ security: { method: secSetup.method, code: secSetup.method === "thumb" ? "" : code } });
    if (ok) { toast.success("Passcode set — locks when your phone boots"); sfx.success(); }
    setSecSetup(null); setSecEntry(""); setSecPat([]);
  };

  return (
    <div className="p-4 flex flex-col gap-4">
      <div>
        <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-white/50 mb-2">Wallpaper</p>
        <div className="grid grid-cols-4 gap-2">
          {Object.entries(WALLPAPERS).map(([k, v]) => (
            <button key={k} data-testid={`settings-wp-${k}`} disabled={busy} onClick={() => { sfx.keyTick(); setCustomUrl(""); save({ wallpaper: k }); }} className={`h-14 rounded-xl border-2 transition-all ${wp === k ? "scale-105" : "border-white/10 opacity-70 hover:opacity-100"}`} style={{ background: v.css, borderColor: wp === k ? acc : undefined }} title={v.label} />
          ))}
        </div>
        <div className="flex gap-2 mt-2">
          <input data-testid="settings-wp-url" value={customUrl} onChange={(e) => setCustomUrl(e.target.value)} placeholder="Custom image URL (https:// or uploaded media)"
            className="flex-1 bg-white/10 border border-white/15 rounded-xl px-3 py-2.5 font-mono text-[10px] text-white focus:outline-none" />
          <button data-testid="settings-wp-apply" disabled={busy || !customUrl.trim()} onClick={() => save({ wallpaper: customUrl.trim() })} className="px-4 rounded-xl font-mono text-[9px] uppercase tracking-[0.15em] text-black font-bold disabled:opacity-40" style={{ backgroundColor: acc }}>Set</button>
        </div>
      </div>
      <div>
        <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-white/50 mb-2">Accent</p>
        <div className="flex gap-2">
          {ACCENTS.map((c) => (
            <button key={c} data-testid={`settings-accent-${c.slice(1)}`} disabled={busy} onClick={() => { sfx.keyTick(); save({ accent: c }); }} className={`w-9 h-9 rounded-full border-2 transition-transform ${acc === c ? "scale-110 border-white" : "border-transparent"}`} style={{ backgroundColor: c }} />
          ))}
        </div>
      </div>
      <div>
        <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-white/50 mb-2">Phone theme</p>
        <div className="grid grid-cols-2 gap-2">
          {Object.entries(PHONE_THEMES).map(([k, v]) => {
            const owned = ownedThemes.includes(k);
            const active = (profile.theme || "stock") === k;
            return (
              <button key={k} data-testid={`settings-theme-${k}`} disabled={busy || !owned}
                onClick={() => { sfx.keyTick(); save({ theme: k }); }}
                className={`rounded-xl border p-3 text-left transition-all ${active ? "bg-white/10" : "border-white/10 hover:bg-white/5"} ${!owned ? "opacity-50" : ""}`}
                style={{ borderColor: active ? acc : undefined }}>
                <span className="block h-8 rounded-lg mb-2 border border-white/10" style={{ background: "linear-gradient(135deg,#1e293b,#2E6BFF 140%)", filter: THEME_FILTERS[k] || undefined }} />
                <span className="font-mono text-[10px] text-white flex items-center gap-1.5">
                  {!owned && <ShieldLock className="w-3 h-3 text-amber-300" />}{v.label}
                </span>
                <span className="font-mono text-[8px] uppercase tracking-[0.1em]" style={{ color: active ? acc : "rgba(255,255,255,0.4)" }}>
                  {active ? "Active" : owned ? "Owned" : `SpecStore · $${v.cost}`}
                </span>
              </button>
            );
          })}
        </div>
        {!demo && <p className="font-mono text-[8px] text-white/35 mt-1.5">Locked themes are earned in the SpecStore — they install like apps.</p>}
      </div>
      <div>
        <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-white/50 mb-2">Ringtone</p>
        <div className="flex flex-col gap-1.5">
          {RINGTONES.map(([k, label]) => (
            <button key={k} data-testid={`settings-ring-${k}`} disabled={busy} onClick={() => { sfx.playRing(k); save({ ringtone: k }); }} className={`flex items-center gap-3 border rounded-xl px-4 py-3 transition-colors ${profile.ringtone === k ? "bg-white/10" : "border-white/10 hover:bg-white/5"}`} style={{ borderColor: profile.ringtone === k ? acc : undefined }}>
              <PhoneIncoming className="w-4 h-4" style={{ color: acc }} />
              <span className="font-mono text-[11px] text-white">{label}</span>
              {profile.ringtone === k && <span className="ml-auto font-mono text-[8px] uppercase tracking-[0.2em]" style={{ color: acc }}>Active</span>}
            </button>
          ))}
        </div>
      </div>
      <div className="flex items-center justify-between rounded-xl bg-white/5 border border-white/10 px-4 py-3">
        <div><p className="font-mono text-[10px] text-white">Notifications</p><p className="font-mono text-[8px] text-white/40">Ringtone & alerts for calls and messages</p></div>
        <button data-testid="settings-notif" disabled={busy} onClick={() => { sfx.keyTick(); save({ notifications: !profile.notifications }); }} className={`w-12 h-7 rounded-full transition-colors relative ${profile.notifications ? "" : "bg-white/15"}`} style={profile.notifications ? { backgroundColor: acc } : {}}>
          <span className={`absolute top-1 w-5 h-5 rounded-full bg-white transition-all ${profile.notifications ? "left-6" : "left-1"}`} />
        </button>
      </div>

      {/* passcode lock */}
      <div className="rounded-xl bg-white/5 border border-white/10 px-4 py-3 flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <ShieldLock className="w-4 h-4" style={{ color: acc }} />
          <p className="font-mono text-[10px] text-white">Passcode lock</p>
          <p data-testid="sec-status" className="ml-auto font-mono text-[8px] uppercase tracking-[0.15em] text-white/40">
            {profile.security?.method ? `on · ${profile.security.method}` : "off"}
          </p>
        </div>
        <div className="flex gap-1.5">
          {[["", "Off"], ["pin", "PIN"], ["pattern", "Pattern"], ["thumb", "Thumb"]].map(([m, lbl]) => (
            <button key={m || "off"} data-testid={`sec-method-${m || "off"}`} disabled={busy}
              onClick={() => { sfx.keyTick(); if (!m) { save({ security: null }); setSecSetup(null); } else setSecSetup({ method: m, first: null, stage: "enter" }); }}
              className={`flex-1 rounded-lg py-2 font-mono text-[8px] uppercase tracking-[0.15em] border transition-colors ${(profile.security?.method || "") === m ? "text-black font-bold" : "border-white/10 text-white/50 hover:text-white"}`}
              style={(profile.security?.method || "") === m ? { backgroundColor: acc, borderColor: acc } : {}}>{lbl}</button>
          ))}
        </div>
        {secSetup && (
          <div data-testid="sec-setup" className="border border-white/10 rounded-xl p-3 flex flex-col items-center gap-2 bg-black/40">
            <p className="font-mono text-[8px] uppercase tracking-[0.2em] text-white/50">
              {secSetup.stage === "enter" ? (secSetup.method === "pin" ? "choose a 4-digit PIN" : secSetup.method === "pattern" ? "draw your pattern (3+ dots)" : "enrol your thumb") : "again, to confirm"}
            </p>
            {secSetup.method === "pin" && <PinPad entry={secEntry} accent={acc} onDelete={() => setSecEntry((p) => p.slice(0, -1))} onDigit={(d) => {
              const next = (secEntry + d).slice(0, 4);
              setSecEntry(next);
              if (next.length === 4) setTimeout(() => secAdvance(next), 150);
            }} />}
            {secSetup.method === "pattern" && <PatternPad points={secPat} accent={acc} size="w-48" onToggle={(i) => setSecPat((p) => [...p, i])} />}
            {secSetup.method === "pattern" && (
              <div className="flex gap-2">
                <button data-testid="sec-pattern-clear" onClick={() => setSecPat([])} className="font-mono text-[8px] uppercase tracking-[0.15em] text-white/40 hover:text-white px-3 py-1.5">Clear</button>
                <button data-testid="sec-pattern-next" disabled={secPat.length < 3} onClick={() => secAdvance(secPat.join(","))}
                  className="font-mono text-[8px] font-bold uppercase tracking-[0.15em] px-4 py-1.5 rounded-full text-black disabled:opacity-40" style={{ backgroundColor: acc }}>
                  {secSetup.stage === "enter" ? "Next" : "Confirm"}
                </button>
              </div>
            )}
            {secSetup.method === "thumb" && <ThumbScan accent={acc} label="hold to enrol" onDone={() => secAdvance("thumb")} />}
            <p data-testid="sec-mismatch" className="font-mono text-[8px] text-red-300 hidden" />
          </div>
        )}
      </div>
      <p className="font-mono text-[8px] text-white/40 leading-relaxed">Changes apply instantly and save to this server — a fresh phone in a new city starts with setup again.</p>
    </div>
  );
}
